/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import useEventCallback from "@mui/utils/useEventCallback";
import { DialogComponent } from "../components/dialog/types";
import {
  DialogsContext,
  OpenDialog,
  OpenDialogOptions,
} from "../components/dialog/dialog-context";

interface DialogStackEntry<P, R> {
  key: string;
  open: boolean;
  Component: DialogComponent<P, R>;
  payload: P;
  promise: Promise<R>;
  resolve: (value: R) => void;
  onClose: (value: R) => Promise<void>;
}

export interface DialogProviderProps {
  children?: React.ReactNode;
  unmountAfter?: number;
}

const DialogProvider = ({
  children,
  unmountAfter = 200,
}: DialogProviderProps) => {
  const [stack, setStack] = React.useState<DialogStackEntry<any, any>[]>([]);
  const keyPrefix = React.useId();
  const nextId = React.useRef(0);

  // WeakMap: dialogPromise → stack entry
  const map = React.useRef(
    new WeakMap<Promise<any>, DialogStackEntry<any, any>>(),
  );

  const open = useEventCallback<OpenDialog>(function openDialog<P, R>(
    Component: DialogComponent<P, R>,
    payload: P,
    options: OpenDialogOptions<R> = {},
  ) {
    const { onClose = async () => {} } = options;

    let resolver!: (value: R) => void;
    const promise = new Promise<R>((resolve) => {
      resolver = resolve;
    });

    const key = `${keyPrefix}-${nextId.current}`;
    nextId.current += 1;

    const entry: DialogStackEntry<P, R> = {
      key,
      open: true,
      Component,
      payload,
      promise,
      resolve: resolver,
      onClose,
    };

    map.current.set(promise, entry);
    setStack((s) => [...s, entry]);

    return promise;
  });

  const closeUi = useEventCallback(function closeUi<R>(dialog: Promise<R>) {
    // Start closing animation
    setStack((prev) =>
      prev.map((d) => (d.promise === dialog ? { ...d, open: false } : d)),
    );

    // Unmount later
    setTimeout(() => {
      setStack((prev) => prev.filter((d) => d.promise !== dialog));
    }, unmountAfter);
  });

  const close = useEventCallback(async function closeDialog<R>(
    dialog: Promise<R>,
    result: R,
  ) {
    const entry = map.current.get(dialog);
    if (!entry) {
      throw new Error("Dialog not found.");
    }

    try {
      await entry.onClose(result);
    } finally {
      entry.resolve(result);
      closeUi(dialog);
    }

    return dialog;
  });

  const value = React.useMemo(() => ({ open, close }), [open, close]);

  return (
    <DialogsContext.Provider value={value}>
      {children}

      {/* Render active dialog components */}
      {stack.map(({ key, open: openD, Component, payload, promise }) => (
        <Component
          key={key}
          open={openD}
          payload={payload}
          onClose={(dialogResult: any) => close(promise, dialogResult)}
        />
      ))}
    </DialogsContext.Provider>
  );
};

export default DialogProvider;
